import React, { useState, useEffect } from "react";
import EditDialogStyle from "styles/application/EditDialogStyle";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import * as yup from "yup";
import { handleValidateObj, generateErrorState } from "helpers/utils";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/system";
import ColourConstants from "helpers/colourConstants";
import { getCMEditReasons } from "services/clients/sites/siteApplications/cmEditReasons";

const AED = EditDialogStyle();

// Additional styled components for this dialog
const OriginalValueContainer = styled("div")({
  backgroundColor: "#f5f5f5",
  padding: "12px 16px",
  borderRadius: "4px",
  marginBottom: "20px",
});

const OriginalValueLabel = styled(Typography)({
  fontFamily: "Roboto Condensed",
  fontSize: 12,
  color: "#666",
  marginBottom: 4,
});

const OriginalValueText = styled(Typography)({
  fontFamily: "Roboto Condensed",
  fontSize: 18,
  fontWeight: "bold",
  color: ColourConstants.commonText,
});

const DataPointInfo = styled("div")({
  marginBottom: "16px",
  paddingBottom: "16px",
  borderBottom: "1px solid #e0e0e0",
});

const InfoRow = styled("div")({
  display: "flex",
  marginBottom: "4px",
});

const InfoLabel = styled(Typography)({
  fontFamily: "Roboto",
  fontSize: 13,
  color: "#666",
  width: "80px",
});

const InfoValue = styled(Typography)({
  fontFamily: "Roboto",
  fontSize: 13,
  color: ColourConstants.commonText,
  fontWeight: 500,
});

const schema = yup.object({
  valueNumeric: yup.number().required("Please enter a value").typeError("Must be a valid number"),
  cmEditReasonID: yup.number().required("Please select a reason").typeError("Please select a reason"),
  otherReason: yup.string(),
});

const defaultErrorSchema = { valueNumeric: null, cmEditReasonID: null, otherReason: null };
const defaultStateSchema = { valueNumeric: "", cmEditReasonID: "", otherReason: "" };

const CMEditDialog = ({ open, closeHandler, dataPoint, onSave, siteAppId, onImageClick, customCaptions }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [input, setInput] = useState(defaultStateSchema);
  const [errors, setErrors] = useState(defaultErrorSchema);
  const [editReasons, setEditReasons] = useState([]);

  // Get original value - use originalValueNumeric if edited before, otherwise current value
  const originalValue = dataPoint?.originalValueNumeric ?? dataPoint?.valueNumeric;

  const selectedReason = editReasons.find(r => r.id === input.cmEditReasonID);
  const isOtherReason = selectedReason?.name?.toLowerCase() === "other";

  const closeOverride = () => {
    setErrors(defaultErrorSchema);
    setInput(defaultStateSchema);
    closeHandler();
  };

  const handleSave = async () => {
    setIsUpdating(true);

    try {
      const localChecker = await handleValidateObj(schema, input);

      // Additional validation for "Other" reason
      if (isOtherReason && !input.otherReason?.trim()) {
        localChecker.push({ name: "otherReason", valid: false, message: "Please provide a reason" });
      }

      if (!localChecker.some((el) => el.valid === false)) {
        if (onSave) {
          await onSave({
            serviceTaskQuestionID: dataPoint?.serviceTaskQuestionID,
            valueNumeric: parseFloat(input.valueNumeric),
            cmEditReasonID: input.cmEditReasonID,
            otherReason: input.otherReason,
          });
        }

        setIsUpdating(false);
        closeOverride();
      } else {
        const newErrors = generateErrorState(localChecker);
        setErrors({ ...errors, ...newErrors });
        setIsUpdating(false);
      }
    } catch (err) {
      console.error("Failed to save CM edit:", err);
      setIsUpdating(false);
    }
  };

  const handleEnterPress = (e) => {
    if (e.keyCode === 13) {
      handleSave();
    }
  };

  // Fetch CM Edit Reasons when dialog opens
  useEffect(() => {
    const fetchEditReasons = async () => {
      if (!siteAppId || !open) return;

      setIsLoading(true);
      try {
        const response = await getCMEditReasons(siteAppId);
        if (response.status) {
          setEditReasons(response.data || []);
        }
      } catch (err) {
        console.error("Failed to fetch CM edit reasons:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEditReasons();
  }, [siteAppId, open]);

  useEffect(() => {
    if (dataPoint && open) {
      setInput({
        valueNumeric: dataPoint.valueNumeric?.toString() || "",
        cmEditReasonID: "",
        otherReason: "",
      });
    }
  }, [dataPoint, open]);

  return (
    <Dialog
      fullWidth={true}
      maxWidth="sm"
      open={open}
      onClose={closeOverride}
      aria-labelledby="cm-edit-dialog-title"
    >
      {isUpdating && <LinearProgress />}

      <AED.ActionContainer>
        <DialogTitle id="cm-edit-dialog-title">
          <AED.HeaderText>Edit CM Data Point</AED.HeaderText>
        </DialogTitle>
        <AED.ButtonContainer>
          <AED.CancelButton
            onClick={closeOverride}
            variant="contained"
            sx={{
              "&.MuiButton-root:hover": {
                backgroundColor: ColourConstants.deleteDialogHover,
                color: "#ffffff",
              },
            }}
          >
            Cancel
          </AED.CancelButton>
          <AED.ConfirmButton
            variant="contained"
            onClick={handleSave}
            sx={{
              "&.MuiButton-root:hover": {
                backgroundColor: ColourConstants.deleteDialogHover,
                color: "#ffffff",
              },
            }}
          >
            Save
          </AED.ConfirmButton>
        </AED.ButtonContainer>
      </AED.ActionContainer>

      <AED.DialogContent>
        {/* Data point context info */}
        <DataPointInfo>
          <InfoRow>
            <InfoLabel>Date:</InfoLabel>
            <InfoValue>{dataPoint?.displayDate || "N/A"}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Asset:</InfoLabel>
            <InfoValue>{dataPoint?.siteAssetName || "N/A"}</InfoValue>
          </InfoRow>
          {dataPoint?.taskQuestionImageURL && (
            <InfoRow>
              <InfoLabel>Image:</InfoLabel>
              <InfoValue>
                <Link
                  component="button"
                  onClick={(e) => {
                    e.preventDefault();
                    if (onImageClick) {
                      onImageClick(dataPoint.taskQuestionImageURL);
                    }
                  }}
                  underline="hover"
                  sx={{ cursor: 'pointer' }}
                >
                  View Image
                </Link>
              </InfoValue>
            </InfoRow>
          )}
          {dataPoint?.defects?.length > 0 && (
            <InfoRow>
              <InfoLabel>{customCaptions?.defect || 'Defect'}:</InfoLabel>
              <InfoValue>
                <Link
                  href={`/app/defects/${dataPoint.defects[0].id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                >
                  View {customCaptions?.defect || 'Defect'}
                </Link>
              </InfoValue>
            </InfoRow>
          )}
        </DataPointInfo>

        {/* Original value display */}
        <OriginalValueContainer>
          <OriginalValueLabel>Original Value</OriginalValueLabel>
          <OriginalValueText>
            {originalValue != null ? originalValue : "N/A"}
          </OriginalValueText>
        </OriginalValueContainer>

        {/* New value input */}
        <AED.InputContainer>
          <div style={{ width: "100%" }}>
            <AED.NameLabel>
              New Value<AED.RequiredStar>*</AED.RequiredStar>
            </AED.NameLabel>
            <AED.NameInput
              error={errors.valueNumeric !== null}
              helperText={errors.valueNumeric}
              variant="outlined"
              type="number"
              value={input.valueNumeric}
              autoFocus
              onKeyDown={handleEnterPress}
              onChange={(e) => setInput({ ...input, valueNumeric: e.target.value })}
              inputProps={{ step: "any" }}
            />
          </div>
        </AED.InputContainer>

        {/* Reason dropdown */}
        <AED.InputContainer>
          <div style={{ width: "100%" }}>
            <AED.NameLabel>
              Reason for Edit<AED.RequiredStar>*</AED.RequiredStar>
            </AED.NameLabel>
            {isLoading ? (
              <div style={{ display: "flex", alignItems: "center", padding: "16px" }}>
                <CircularProgress size={20} style={{ marginRight: 8 }} />
                <Typography variant="body2">Loading reasons...</Typography>
              </div>
            ) : (
              <TextField
                error={errors.cmEditReasonID !== null}
                helperText={errors.cmEditReasonID}
                fullWidth
                select
                value={input.cmEditReasonID}
                onChange={(e) => {
                  const newReasonId = e.target.value;
                  const newReason = editReasons.find(r => r.id === newReasonId);
                  const isNewReasonOther = newReason?.name?.toLowerCase() === "other";
                  setInput({
                    ...input,
                    cmEditReasonID: newReasonId,
                    otherReason: isNewReasonOther ? input.otherReason : ""
                  });
                }}
                variant="outlined"
              >
                <MenuItem value="" disabled>
                  Select a reason...
                </MenuItem>
                {editReasons.map((reason) => (
                  <MenuItem key={reason.id} value={reason.id}>
                    {reason.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </div>
        </AED.InputContainer>

        {/* Other reason text field - only shown when "Other" is selected */}
        {isOtherReason && (
          <AED.InputContainer>
            <div style={{ width: "100%" }}>
              <AED.NameLabel>
                Please Specify<AED.RequiredStar>*</AED.RequiredStar>
              </AED.NameLabel>
              <TextField
                error={errors.otherReason !== null}
                helperText={errors.otherReason}
                fullWidth
                multiline
                rows={3}
                value={input.otherReason}
                onChange={(e) => setInput({ ...input, otherReason: e.target.value })}
                variant="outlined"
                placeholder="Please explain why this value is being edited..."
                inputProps={{ maxLength: 500 }}
              />
            </div>
          </AED.InputContainer>
        )}

        {/* Show previous edit info if this point was already edited */}
        {dataPoint?.isEdited && (
          <div style={{
            marginTop: "16px",
            padding: "12px",
            backgroundColor: "#FFF3E0",
            borderRadius: "4px",
            border: "1px solid #FFE0B2"
          }}>
            <Typography variant="body2" sx={{ fontWeight: 500, marginBottom: "4px" }}>
              Previously Edited
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "12px", color: "#666" }}>
              By {dataPoint.modifiedByUserName} on {dataPoint.modifiedDateTime}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "12px", color: "#666" }}>
              Reason: {dataPoint.editReasonName}
              {dataPoint.editOtherReason && ` - ${dataPoint.editOtherReason}`}
            </Typography>
          </div>
        )}
      </AED.DialogContent>
    </Dialog>
  );
};

export default CMEditDialog;
